'use client';

import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslations } from 'next-intl';
import { AppDispatch } from '@/app/store';
import {
  acceptAll,
  rejectAll,
  openPreferences,
  selectHasResponded,
  selectIsConsentInitialized,
} from '@/app/store/slices/cookieConsentSlice';
import { Button } from '@/components/ui/button';
import { Cookie } from 'lucide-react';

// ─── CookieBanner ─────────────────────────────────────────────────────────────

export const CookieBanner = React.memo(() => {
  const t = useTranslations('cookieConsent.banner');
  const dispatch = useDispatch<AppDispatch>();
  const hasResponded = useSelector(selectHasResponded);
  const isInitialized = useSelector(selectIsConsentInitialized);

  // Don't render until we've read localStorage (avoids SSR flash)
  if (!isInitialized || hasResponded) return null;

  return (
    <div
      role="dialog"
      aria-modal="false"
      aria-label={t('title')}
      className="fixed bottom-0 left-0 right-0 z-50 animate-in slide-in-from-bottom-4 duration-300"
    >
      {/* Backdrop blur line */}
      <div className="border-t border-border bg-background/95 backdrop-blur-md shadow-[0_-4px_24px_rgba(0,0,0,0.08)]">
        <div className="mx-auto max-w-7xl px-4 py-4 sm:px-6 lg:px-8">
          <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
            {/* Text */}
            <div className="flex items-start gap-3 min-w-0">
              <Cookie className="h-5 w-5 mt-0.5 shrink-0 text-muted-foreground" />
              <div className="min-w-0">
                <p className="text-sm font-semibold text-foreground">{t('title')}</p>
                <p className="text-xs text-muted-foreground mt-0.5 leading-relaxed">
                  {t('description')}
                </p>
              </div>
            </div>

            {/* Actions */}
            <div className="flex flex-wrap items-center gap-2 shrink-0">
              <Button
                variant="ghost"
                size="sm"
                className="text-xs text-muted-foreground hover:text-foreground"
                onClick={() => dispatch(rejectAll())}
              >
                {t('rejectAll')}
              </Button>
              <Button
                variant="outline"
                size="sm"
                className="text-xs"
                onClick={() => dispatch(openPreferences())}
              >
                {t('managePreferences')}
              </Button>
              <Button
                size="sm"
                className="text-xs"
                onClick={() => dispatch(acceptAll())}
              >
                {t('acceptAll')}
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
});
CookieBanner.displayName = 'CookieBanner';
